import axios from 'axios';
import type { AxiosInstance, AxiosError } from 'axios';

// Use relative path so Vite proxy works
const API_URL = '';

export interface ChatRequest {
  message: string;
  conversation_id?: string;
  language?: string;
}

export type { ChatRequest as ChatRequestType };

export interface ChatResponse {
  response: string;
  conversation_id: string;
  visemes?: Viseme[];
  booking_ready?: boolean;
  booking_data?: {
    boat_model: string;
    boat_length: number;
    boat_width: number;
    boat_draft: number;
    check_in_date: string;
    check_out_date: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    mooring_name?: string;
    notes?: string;
  };
}

export interface Viseme {
  time: number;
  shape: string;
}

export interface AudioRequest {
  audio_base64: string;
  language?: string;
  mime_type?: string;
}

export interface SynthesizeRequest {
  text: string;
  language?: string;
}

export interface SynthesizeResponse {
  audio_url?: string;
  audio_base64?: string;
  duration: number;
  visemes: Viseme[];
}

export interface BookingData {
  boat_model: string;
  boat_length: number;
  boat_width: number;
  boat_draft: number;
  mooring_name: string;
  check_in_date: string;
  check_out_date: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  notes?: string;
}

export interface SearchMooringsRequest {
  checkInDate: string;
  checkOutDate: string;
  boatLength: number;
  boatWidth: number;
  boatDraft: number;
}

export interface SearchMooringsResponse {
  available: any[];
  all: any[];
}

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
      }
    );
  }

  /**
   * Health check
   */
  async health(): Promise<{ status: string }> {
    const response = await this.client.get('/health');
    return response.data;
  }

  /**
   * Send chat message and get response
   */
  async chat(request: ChatRequest): Promise<ChatResponse> {
    const response = await this.client.post<ChatResponse>('/api/chat', request);
    return response.data;
  }

  /**
   * Transcribe audio to text
   */
  async transcribe(request: AudioRequest): Promise<{ text: string }> {
    const response = await this.client.post<{ text: string }>('/api/transcribe', request);
    return response.data;
  }

  /**
   * Synthesize text to speech with visemes
   */
  async synthesize(request: SynthesizeRequest): Promise<SynthesizeResponse> {
    const response = await this.client.post<SynthesizeResponse>('/api/synthesize', request);
    return response.data;
  }

  /**
   * Submit booking
   */
  async submitBooking(data: BookingData): Promise<{ success: boolean; booking_id: string; message: string }> {
    const response = await this.client.post('/api/booking/submit', data);
    return response.data;
  }

  /**
   * Get available moorings
   */
  async getMoorings(): Promise<{ moorings: any[] }> {
    const response = await this.client.get('/api/moorings');
    return response.data;
  }

  /**
   * Search for available moorings
   */
  async searchMoorings(request: SearchMooringsRequest): Promise<SearchMooringsResponse> {
    const response = await this.client.post<SearchMooringsResponse>('/api/moorings/search', request);
    return response.data;
  }

  /**
   * Submit chat-based booking
   */
  async submitChatBooking(data: {
    boat_model: string;
    boat_length: number;
    boat_width: number;
    boat_draft: number;
    check_in_date: string;
    check_out_date: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    mooring_name?: string;
    notes?: string;
  }): Promise<{ success: boolean; booking_id: string; message?: string; price?: any }> {
    const response = await this.client.post('/api/chat-booking', data);
    return response.data;
  }
}

export const apiClient = new ApiClient();
